import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Zap, BrainCircuit, Lock, Loader2, Sparkles, Info, Crown, Palette, Volume2, Phone } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";
import { LegalConsentModal } from "@/components/LegalConsentModal";
import { speak } from "@/lib/voice";

const VOICE_PRESETS = {
  nino: { pitch: 1.5, rate: 1.05 },
  nina: { pitch: 1.7, rate: 1.05 },
  mujer: { pitch: 1.05, rate: 1.0 },
  hombre: { pitch: 0.8, rate: 0.95 },
};

const ROBOT = "/robots/hero_robot.png";

const STYLES = {
  neon_violet: { hue: 0, glow: "rgba(176,38,255,0.45)", accent: "#B026FF" },
  cyber_cyan: { hue: 160, glow: "rgba(0,245,255,0.45)", accent: "#00F5FF" },
  emerald_core: { hue: 250, glow: "rgba(0,255,170,0.45)", accent: "#00FFAA" },
  solar_gold: { hue: 60, glow: "rgba(255,200,60,0.45)", accent: "#FFC83C" },
};

const DEFAULT_MATRIX = { social_energy: 50, professional_tone: 50, risk_tolerance: 50, pitch_style: 50 };
const DEFAULT_PHYSICAL = { age: "", height_cm: "", weight_kg: "", hair_color: "", eye_color: "", skin_tone: "" };
const DEFAULT_CHARM = { mystique: 5, charisma: 5, humor: 5, empathy: 5, depth: 5 };
const CHARM_ORDER = ["mystique", "charisma", "humor", "empathy", "depth"];

export const PersonalityLab = ({ t, user, setUser }) => {
  const lab = t.lab;
  const g = lab.growth;
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [synced, setSynced] = useState(false);
  const prevStageRef = useRef(user.avatar_growth?.stage ?? 0);

  const growth = user.avatar_growth || { score: 0, stage: 0, stage_id: "egg", img: "/robots/skins/egg.webp" };
  const navigate = useNavigate();
  const currentVoice = user.avatar_voice?.voice_id || "mujer";
  const [lang] = useState(document.documentElement.lang === "en" ? "en" : "es");

  const previewVoice = (id) => {
    speak(lab.voicePreview, lang, VOICE_PRESETS[id]);
  };

  const selectVoice = async (id) => {
    previewVoice(id);
    try {
      const { data } = await api.put("/users/profile", { avatar_voice: id });
      setUser(data);
      toast.success(lab.voiceSaved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (growth.stage > prevStageRef.current) {
      toast.success(`${g.evolved} ${g.stageNames[growth.stage_id] || ""}`);
    }
    prevStageRef.current = growth.stage;
  }, [growth.stage, growth.stage_id, g]);

  const growthTasks = [
    { key: "name", done: !!(user.avatar_name || "").trim() },
    { key: "skin", done: !!user.avatar_skin },
    { key: "charm", done: !!user.avatar_charm && Object.values(user.avatar_charm).some((v) => Number(v) !== 5) },
    { key: "objectives", done: (user.avatar_objectives || []).length > 0 },
    { key: "bio", done: !!(user.avatar_bio || "").trim() },
    { key: "physical", done: Object.values(user.avatar_physical || {}).some((v) => v) },
  ];
  const [form, setForm] = useState({
    avatar_name: user.avatar_name || "",
    avatar_visual_style: user.avatar_visual_style || "neon_violet",
    avatar_mirror_factor: user.avatar_mirror_factor ?? 50,
    avatar_traits: { ...DEFAULT_MATRIX, ...(user.avatar_traits || {}) },
    avatar_objectives: user.avatar_objectives || [],
    avatar_bio: user.avatar_bio || "",
    avatar_physical: { ...DEFAULT_PHYSICAL, ...(user.avatar_physical || {}) },
    avatar_charm: { ...DEFAULT_CHARM, ...(user.avatar_charm || {}) },
  });
  const [charmQuote, setCharmQuote] = useState("idle");
  const [charmBusy, setCharmBusy] = useState(false);
  const [skinData, setSkinData] = useState(null);
  const [skinBusy, setSkinBusy] = useState(false);
  const [unlockBusy, setUnlockBusy] = useState(false);

  useEffect(() => {
    api.get("/avatar/skins").then(({ data }) => setSkinData(data)).catch(() => {});
  }, [user.premium_customization]);

  const selectSkin = async (skin) => {
    if (skin.locked) {
      toast.error(lab.skinLockedToast);
      return;
    }
    setSkinBusy(true);
    try {
      const { data } = await api.put("/users/profile", { avatar_skin: skin.id });
      setUser(data);
      toast.success(lab.skinSaved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSkinBusy(false);
    }
  };

  const selectAura = async (color) => {
    if (!skinData?.premium_customization) {
      toast.error(lab.auraLocked);
      return;
    }
    try {
      const { data } = await api.put("/users/profile", { avatar_aura: color });
      setUser(data);
      toast.success(lab.auraSaved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [showLegal, setShowLegal] = useState(false);

  const unlockPremium = async () => {
    if (!user.legal_accepted) {
      setShowLegal(true);  // legal consent happens AT PAYMENT time
      return;
    }
    setUnlockBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "premium_customization",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setUnlockBusy(false);
    }
  };

  const setCharm = (key, val) => {
    setForm((f) => ({ ...f, avatar_charm: { ...f.avatar_charm, [key]: val } }));
    setCharmQuote(val >= 8 ? `${key}_hi` : val <= 2 ? `${key}_lo` : "mid");
  };

  const saveCharm = async () => {
    setCharmBusy(true);
    try {
      const { data } = await api.put("/users/profile", { avatar_charm: form.avatar_charm });
      setUser(data);
      toast.success(lab.charmSaved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCharmBusy(false);
    }
  };

  const style = STYLES[form.avatar_visual_style] || STYLES.neon_violet;

  const setPhys = (key, val) =>
    setForm((f) => ({ ...f, avatar_physical: { ...f.avatar_physical, [key]: val } }));

  const toggleChip = (key) =>
    setForm((f) => ({
      ...f,
      avatar_objectives: f.avatar_objectives.includes(key)
        ? f.avatar_objectives.filter((o) => o !== key)
        : [...f.avatar_objectives, key],
    }));

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/users/profile", form);
      setUser(data);
      setSynced(true);
      setTimeout(() => setSynced(false), 2200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="personality-lab">
      {showLegal && (
        <LegalConsentModal lang={lang} user={user} setUser={setUser}
          onDone={() => { setShowLegal(false); unlockPremium(); }}
          onCancel={() => setShowLegal(false)} />
      )}
      {/* ===== EVOLUTION / BIRTH EFFECT CARD ===== */}
      <div className="relative bg-[#0F0F1A]/80 border border-[#FF6EC7]/25 rounded-3xl p-6 mb-6 overflow-hidden" data-testid="growth-card">
        <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#FF6EC7] blur-[100px] opacity-15 pointer-events-none" />
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <motion.img
            key={growth.stage_id}
            src={growth.img}
            alt={growth.stage_id}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, y: [0, -8, 0] }}
            transition={{ scale: { duration: 0.5, type: "spring" }, y: { duration: 3.5, repeat: Infinity, ease: "easeInOut" } }}
            className="w-28 h-28 object-contain shrink-0 drop-shadow-[0_0_25px_rgba(255,110,199,0.45)]"
            data-testid="growth-stage-img"
          />
          <div className="flex-1 w-full">
            <p className="text-[9px] uppercase tracking-[0.25em] text-[#FF6EC7] font-bold mb-1">{g.title}</p>
            <h3 className="font-heading font-black text-lg text-white mb-1" data-testid="growth-stage-name">
              {g.stageNames[growth.stage_id] || growth.stage_id}
            </h3>
            <p className="text-[11px] text-[#A0A0B0] leading-relaxed mb-3">{g.explain}</p>
            {/* Progress bar with 4 milestones */}
            <div className="relative h-2.5 rounded-full bg-white/5 border border-white/10 overflow-visible mb-2">
              <motion.div
                animate={{ width: `${Math.max(4, growth.score)}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="h-full rounded-full bg-gradient-to-r from-[#FF6EC7] via-[#B026FF] to-[#00F5FF] shadow-[0_0_12px_rgba(255,110,199,0.6)]"
                data-testid="growth-progress-bar"
              />
              {[0, 25, 60, 90].map((m) => (
                <span
                  key={m}
                  className={`absolute -top-[3px] w-3.5 h-3.5 rounded-full border-2 ${growth.score >= m ? "bg-[#FF6EC7] border-white/60" : "bg-[#0F0F1A] border-white/20"}`}
                  style={{ left: `calc(${m}% - 7px)` }}
                />
              ))}
            </div>
            <p className="text-[10px] font-black text-white/50 mb-3">{g.progress}: <span className="text-[#FF6EC7]">{growth.score}/100</span></p>
            <div className="flex flex-wrap gap-1.5" data-testid="growth-tasks">
              {growthTasks.map(({ key, done }) => (
                <span
                  key={key}
                  className={`text-[9px] font-bold px-2.5 py-1 rounded-full border ${
                    done ? "text-[#00FFAA] border-[#00FFAA]/40 bg-[#00FFAA]/8" : "text-white/35 border-white/10 bg-white/[0.03]"
                  }`}
                >
                  {done ? "✓ " : "○ "}{g.tasks[key]}
                </span>
              ))}
            </div>
            {growth.stage >= 2 && !user.onboarded && (
              <button
                data-testid="neural-sync-cta"
                onClick={() => navigate("/awakening")}
                className="mt-4 inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#05050A] bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] shadow-[0_0_22px_rgba(0,255,170,0.5)] hover:shadow-[0_0_35px_rgba(0,255,170,0.75)] hover:-translate-y-0.5 transition-all animate-neon-pulse"
                title={lab.neuralSyncHint}
              >
                <Phone size={13} className="fill-[#05050A]" /> {lab.neuralSyncBtn}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto">
        {lab.steps.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} data-testid={`lab-step-${i}`}
            className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border transition-colors ${
              i === step ? "bg-[#B026FF]/20 border-[#B026FF]/60 text-white" : "border-white/10 text-white/35 hover:text-white/70"
            }`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      <div className="relative bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6 sm:p-8 overflow-hidden">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="lab-step1">
              <h2 className="font-heading font-black text-lg mb-6">{lab.step1}</h2>
              <div className="flex flex-col sm:flex-row gap-6 items-center mb-8">
                <motion.img
                  src={(skinData?.skins || []).find((s) => s.id === user.avatar_skin)?.img || ROBOT}
                  alt="Avatar preview"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="w-32 h-32 object-contain shrink-0 transition-all duration-500"
                  style={{ filter: user.avatar_skin ? `drop-shadow(0 0 24px ${user.avatar_aura || style.accent})` : `hue-rotate(${style.hue}deg) drop-shadow(0 0 24px ${user.avatar_aura || style.glow})` }}
                  data-testid="robot-preview"
                />
                <div className="flex-1 w-full space-y-5">
                  <input data-testid="lab-avatar-name" value={form.avatar_name}
                    onChange={(e) => setForm({ ...form, avatar_name: e.target.value })}
                    placeholder="Avatar name"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B026FF]/60" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3">{lab.visualStyle}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(lab.styles).map(([key, label]) => (
                        <button key={key} type="button" data-testid={`style-${key}`}
                          onClick={() => setForm({ ...form, avatar_visual_style: key })}
                          className={`p-2.5 rounded-xl border text-[11px] font-bold flex items-center gap-2 transition-all ${
                            form.avatar_visual_style === key ? "border-white/40 bg-white/10 text-white" : "border-white/10 bg-white/5 text-white/50"
                          }`}>
                          <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: STYLES[key].accent, boxShadow: `0 0 8px ${STYLES[key].glow}` }} />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="mirror-factor-block">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-xs font-bold text-white uppercase tracking-widest">{lab.mirrorFactor}</p>
                  <span className="font-heading font-black text-lg" style={{ color: style.accent }}>{form.avatar_mirror_factor}%</span>
                </div>
                <p className="text-[11px] text-[#A0A0B0] mb-4">{lab.mirrorExplain}</p>
                <input type="range" min="0" max="100" value={form.avatar_mirror_factor}
                  onChange={(e) => setForm({ ...form, avatar_mirror_factor: Number(e.target.value) })}
                  className="w-full accent-[#B026FF] cursor-pointer" data-testid="mirror-factor-slider" />
              </div>

              {/* ===== AVATAR GALLERY ===== */}
              {skinData && (
                <div className="mt-8" data-testid="avatar-gallery">
                  <p className="text-xs font-bold text-white uppercase tracking-widest mb-1 flex items-center gap-2">
                    <Palette size={14} className="text-[#00F5FF]" /> {lab.gallery}
                  </p>
                  <p className="text-[11px] text-[#A0A0B0] mb-4">{lab.gallerySub}</p>

                  {skinData.premium_customization ? (
                    <p className="text-[10px] font-bold text-[#FFD700] bg-[#FFD700]/5 border border-[#FFD700]/30 rounded-xl px-4 py-2.5 mb-4 inline-flex items-center gap-2" data-testid="premium-active-banner">
                      <Crown size={12} /> {lab.unlockedBanner}
                    </p>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-[#FFD700]/5 border border-[#FFD700]/30 rounded-2xl px-5 py-4 mb-4" data-testid="unlock-premium-banner">
                      <div className="flex-1">
                        <p className="text-xs font-black text-[#FFD700] flex items-center gap-1.5"><Crown size={13} /> {lab.unlockTitle}</p>
                        <p className="text-[10px] text-white/50 mt-1">{lab.unlockDesc}</p>
                      </div>
                      <button
                        data-testid="unlock-premium-btn"
                        onClick={unlockPremium} disabled={unlockBusy}
                        className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#05050A] bg-gradient-to-r from-[#FFD700] to-[#FFC83C] shadow-[0_0_20px_rgba(255,215,0,0.4)] hover:shadow-[0_0_30px_rgba(255,215,0,0.65)] hover:-translate-y-0.5 transition-all disabled:opacity-60"
                      >
                        {unlockBusy ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} className="fill-[#05050A]" />}
                        {unlockBusy ? lab.unlocking : lab.unlockBtn}
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {skinData.skins.map((s) => {
                      const selected = user.avatar_skin === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`skin-${s.id}`}
                          onClick={() => selectSkin(s)}
                          disabled={skinBusy}
                          className={`relative rounded-2xl border overflow-hidden text-left transition-all group ${
                            selected
                              ? "border-[#00F5FF] shadow-[0_0_20px_rgba(0,245,255,0.4)]"
                              : s.locked ? "border-white/10 opacity-80" : "border-white/10 hover:border-white/30"
                          }`}
                        >
                          <div className="relative h-28 sm:h-32 flex items-center justify-center bg-[radial-gradient(circle_at_50%_60%,rgba(176,38,255,0.14),transparent_70%)]">
                            <img
                              src={s.img}
                              alt={s.id}
                              loading="lazy"
                              className={`h-full w-auto object-contain py-1.5 drop-shadow-[0_0_16px_rgba(176,38,255,0.5)] transition-transform group-hover:scale-110 ${s.locked ? "grayscale opacity-70" : ""}`}
                            />
                            {s.locked && (
                              <div className="absolute inset-0 bg-[#05050A]/55 flex items-center justify-center">
                                <span className="w-8 h-8 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/50 flex items-center justify-center text-[#FFD700]">
                                  <Lock size={13} />
                                </span>
                              </div>
                            )}
                            {selected && (
                              <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#00F5FF] text-[#05050A] flex items-center justify-center">
                                <Check size={12} />
                              </span>
                            )}
                          </div>
                          <div className="px-2.5 py-2 bg-[#0F0F1A] flex items-center justify-between gap-1">
                            <span className="text-[10px] font-black text-white truncate">{lab.skinNames[s.id] || s.id}</span>
                            <span className={`text-[7px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full border shrink-0 ${
                              s.free ? "text-[#00FFAA] border-[#00FFAA]/40 bg-[#00FFAA]/8" : "text-[#FFD700] border-[#FFD700]/40 bg-[#FFD700]/8"
                            }`}>
                              {s.free ? lab.freeChip : lab.premiumChip}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* ===== NEON AURA (premium) ===== */}
                  <div className="mt-6" data-testid="aura-picker">
                    <p className="text-xs font-bold text-white uppercase tracking-widest mb-1 flex items-center gap-2">
                      <Sparkles size={13} className="text-[#FF6EC7]" /> {lab.auraTitle}
                      {!skinData.premium_customization && <Lock size={11} className="text-[#FFD700]" />}
                    </p>
                    {!skinData.premium_customization && <p className="text-[10px] text-white/40 mb-3">{lab.auraLocked}</p>}
                    <div className={`flex flex-wrap gap-3 mt-3 ${!skinData.premium_customization ? "opacity-50" : ""}`}>
                      {skinData.aura_colors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          data-testid={`aura-${color.replace("#", "")}`}
                          onClick={() => selectAura(color)}
                          className={`w-9 h-9 rounded-full border-2 transition-all hover:scale-110 ${
                            user.avatar_aura === color ? "border-white scale-110" : "border-transparent"
                          }`}
                          style={{ backgroundColor: color, boxShadow: `0 0 14px ${color}88` }}
                        />
                      ))}
                    </div>
                  </div>
                  {/* ===== AVATAR VOICE (born voice) ===== */}
                  <div className="mt-6" data-testid="voice-picker">
                    <p className="text-xs font-bold text-white uppercase tracking-widest mb-1 flex items-center gap-2">
                      <Volume2 size={13} className="text-[#00FFAA]" /> {lab.voiceTitle}
                    </p>
                    <p className="text-[10px] text-white/40 mb-3">{lab.voiceSub}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {Object.entries(lab.voices).map(([id, label]) => {
                        const selected = currentVoice === id;
                        return (
                          <button
                            key={id}
                            type="button"
                            data-testid={`voice-${id}`}
                            onClick={() => selectVoice(id)}
                            className={`relative rounded-2xl border px-3 py-3.5 text-left transition-all ${
                              selected
                                ? "border-[#00FFAA] bg-[#00FFAA]/8 shadow-[0_0_16px_rgba(0,255,170,0.3)]"
                                : "border-white/10 bg-white/[0.03] hover:border-white/30"
                            }`}
                          >
                            <p className={`text-[11px] font-black mb-1 ${selected ? "text-[#00FFAA]" : "text-white"}`}>{label}</p>
                            <span
                              onClick={(e) => { e.stopPropagation(); previewVoice(id); }}
                              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-[#00F5FF]"
                            >
                              <Volume2 size={10} /> {lab.listenBtn}
                            </span>
                            {selected && (
                              <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-[#00FFAA] text-[#05050A] flex items-center justify-center">
                                <Check size={10} />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="lab-step2">
              <h2 className="font-heading font-black text-lg mb-1">{lab.attractionTitle}</h2>
              <p className="text-[11px] text-[#A0A0B0] mb-6">{lab.attractionSub}</p>

              {/* Live robot preview + micro-feedback quote */}
              <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4 mb-7" data-testid="charm-preview">
                <motion.img
                  src={ROBOT} alt="Charm preview"
                  animate={{ y: [0, -8, 0], rotate: [0, 2, -2, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="w-16 sm:w-20 shrink-0"
                  style={{ filter: `hue-rotate(${style.hue}deg) drop-shadow(0 0 18px ${style.glow})` }}
                />
                <AnimatePresence mode="wait">
                  <motion.p
                    key={charmQuote}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    className="flex-1 text-xs italic leading-relaxed px-4 py-3 rounded-2xl rounded-bl-sm bg-[#B026FF]/10 border border-[#B026FF]/25"
                    style={{ color: style.accent }}
                    data-testid="charm-quote"
                  >
                    {`"${lab.charmQuotes[charmQuote] || lab.charmQuotes.idle}"`}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* 5 Cautivation Sliders 0-10 */}
              <div className="space-y-6">
                {CHARM_ORDER.map((key) => {
                  const cfg = lab.charmSliders[key];
                  const val = form.avatar_charm[key];
                  return (
                    <div key={key} data-testid={`charm-${key}`}>
                      <p className="text-xs font-bold text-white uppercase tracking-widest mb-2 flex items-center justify-between">
                        {cfg.label}
                        <span
                          className="font-heading font-black text-base w-9 h-9 rounded-xl flex items-center justify-center border"
                          style={{ color: style.accent, borderColor: `${style.accent}50`, backgroundColor: `${style.accent}12`, boxShadow: `0 0 14px ${style.glow}` }}
                          data-testid={`charm-${key}-value`}
                        >
                          {val}
                        </span>
                      </p>
                      <input
                        type="range" min="0" max="10" step="1" value={val}
                        onChange={(e) => setCharm(key, Number(e.target.value))}
                        className="w-full accent-[#00F5FF] cursor-pointer"
                        data-testid={`charm-${key}-slider`}
                      />
                      <div className="flex justify-between mt-1.5 gap-4">
                        <span className={`text-[10px] leading-tight max-w-[45%] ${val <= 3 ? "text-[#00F5FF] font-bold" : "text-white/30"}`}>0 · {cfg.left}</span>
                        <span className={`text-[10px] leading-tight max-w-[45%] text-right ${val >= 7 ? "text-[#D080FF] font-bold" : "text-white/30"}`}>{cfg.right} · 10</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Submit & lock */}
              <button
                data-testid="update-charm-btn"
                onClick={saveCharm} disabled={charmBusy}
                className="w-full mt-8 py-4 rounded-2xl font-heading font-black text-sm uppercase tracking-[0.2em] bg-gradient-to-r from-[#B026FF] to-[#00F5FF] shadow-[0_0_30px_rgba(176,38,255,0.5)] hover:shadow-[0_0_45px_rgba(176,38,255,0.75)] hover:-translate-y-0.5 transition-all disabled:opacity-60 flex items-center justify-center gap-3"
              >
                {charmBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {charmBusy ? lab.updatingCharm : lab.updateCharm}
              </button>
              <p className="flex items-start gap-2 mt-4 text-[10px] text-[#FFC83C]/90 bg-[#FFC83C]/5 border border-[#FFC83C]/25 rounded-xl px-4 py-3" data-testid="charm-tooltip">
                <Info size={13} className="shrink-0 mt-0.5" /> {lab.charmTip}
              </p>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="sphys" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="lab-step-physical">
              <h2 className="font-heading font-black text-lg mb-1">{lab.stepPhys}</h2>
              <p className="text-[11px] text-[#A0A0B0] mb-7">{lab.physSub}</p>
              <div className="grid grid-cols-3 gap-3 mb-7">
                {[
                  { key: "age", label: lab.physAge, min: 18, max: 99 },
                  { key: "height_cm", label: lab.physHeight, min: 100, max: 230 },
                  { key: "weight_kg", label: lab.physWeight, min: 30, max: 250 },
                ].map(({ key, label, min, max }) => (
                  <div key={key}>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-2">{label}</p>
                    <input type="number" min={min} max={max} value={form.avatar_physical[key]}
                      onChange={(e) => setPhys(key, e.target.value)}
                      data-testid={`phys-${key}-input`}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-sm text-center focus:outline-none focus:border-[#00F5FF]/50" />
                  </div>
                ))}
              </div>
              {[
                { key: "hair_color", label: lab.physHair, opts: lab.hairOpts },
                { key: "eye_color", label: lab.physEyes, opts: lab.eyeOpts },
                { key: "skin_tone", label: lab.physSkin, opts: lab.skinOpts },
              ].map(({ key, label, opts }) => (
                <div key={key} className="mb-6" data-testid={`phys-${key}-group`}>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3">{label}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(opts).map(([val, optLabel]) => {
                      const on = form.avatar_physical[key] === val;
                      return (
                        <button key={val} type="button" data-testid={`phys-${key}-${val}`}
                          onClick={() => setPhys(key, on ? "" : val)}
                          className={`px-4 py-2 rounded-full text-[11px] font-bold border transition-all ${
                            on ? "border-[#00F5FF]/60 bg-[#00F5FF]/10 text-[#00F5FF] shadow-[0_0_12px_rgba(0,245,255,0.25)]" : "border-white/15 bg-white/5 text-white/50 hover:text-white"
                          }`}>
                          {optLabel}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="lab-step3">
              <h2 className="font-heading font-black text-lg mb-6">{lab.step3}</h2>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3">{lab.activeModes}</p>
              <div className="flex flex-wrap gap-2.5 mb-8">
                {Object.entries(lab.chips).map(([key, label]) => {
                  const on = form.avatar_objectives.includes(key);
                  return (
                    <button key={key} type="button" data-testid={`chip-${key}`} onClick={() => toggleChip(key)}
                      className={`px-5 py-2.5 rounded-full text-xs font-bold border transition-all flex items-center gap-2 ${
                        on
                          ? "border-[#00FFAA]/60 bg-[#00FFAA]/10 text-[#00FFAA] shadow-[0_0_15px_rgba(0,255,170,0.25)]"
                          : "border-white/15 bg-white/5 text-white/50 hover:text-white"
                      }`}>
                      {on && <Check size={13} />}
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 flex items-center gap-2">
                <BrainCircuit size={14} className="text-[#00F5FF]" /> {lab.secretsTitle}
              </p>
              <textarea data-testid="secrets-textarea" rows={5} value={form.avatar_bio}
                onChange={(e) => setForm({ ...form, avatar_bio: e.target.value })}
                placeholder={lab.secretsPlaceholder}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#00F5FF]/50 placeholder:text-white/25 resize-none mb-5" />
              <div className="bg-[#00FFAA]/5 border border-[#00FFAA]/30 rounded-2xl p-5 mb-6" data-testid="privacy-badge">
                <p className="text-xs font-bold text-[#00FFAA] uppercase tracking-widest flex items-center gap-2 mb-2">
                  <Lock size={14} /> {lab.privacyTitle}
                </p>
                <p className="text-[11px] text-[#A0A0B0] leading-relaxed">{lab.privacyCopy}</p>
              </div>
              <button data-testid="sync-activate-btn" onClick={sync} disabled={busy}
                className="w-full py-5 rounded-2xl font-heading font-black text-sm uppercase tracking-[0.2em] bg-gradient-to-r from-[#B026FF] via-[#7B1FD6] to-[#00F5FF] shadow-[0_0_35px_rgba(176,38,255,0.5)] hover:shadow-[0_0_50px_rgba(176,38,255,0.75)] hover:-translate-y-0.5 transition-all disabled:opacity-60 flex items-center justify-center gap-3 animate-neon-pulse">
                <Zap size={18} className="fill-white" />
                {busy ? lab.syncing : lab.syncBtn}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {synced && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 bg-[#05050A]/85 backdrop-blur-md flex flex-col items-center justify-center rounded-3xl"
              data-testid="brain-synced-overlay">
              <motion.div initial={{ scale: 0.4 }} animate={{ scale: [0.4, 1.15, 1] }} transition={{ duration: 0.5 }}
                className="w-20 h-20 rounded-full bg-[#00FFAA]/15 border-2 border-[#00FFAA] flex items-center justify-center shadow-[0_0_40px_rgba(0,255,170,0.5)] mb-5">
                <BrainCircuit size={38} className="text-[#00FFAA]" />
              </motion.div>
              <p className="font-heading font-black text-[#00FFAA] text-lg">{lab.synced}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex justify-between mt-5">
        <button data-testid="lab-back-btn" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          className="px-6 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-white/5 border border-white/15 disabled:opacity-30">
          {lab.back}
        </button>
        {step < 3 && (
          <button data-testid="lab-next-btn" onClick={() => setStep(step + 1)}
            className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-[#B026FF] shadow-[0_0_20px_rgba(176,38,255,0.45)]">
            {lab.next}
          </button>
        )}
      </div>
    </div>
  );
};
